import { useState, useEffect, useCallback } from "react";
import { getAccount } from "../../services/portalService";
import { errorToast } from "../Toasts/Toasts";

// A society's account: what we have quoted them, what we have invoiced, and
// what is still owed.
//
// Every figure is Zoho Books'. Nothing is worked out here - a balance this
// page calculated would be a second opinion about money, and the wrong one
// the moment a payment is recorded in Books. When Books cannot be reached the
// page says so rather than showing a confident zero.

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const when = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

const STATUS = {
  paid: "success",
  sent: "primary",
  overdue: "danger",
  partially_paid: "warning text-dark",
  draft: "secondary",
  invoiced: "success",
  declined: "secondary",
  expired: "secondary",
};

const AccountPage = () => {
  const [a, setA] = useState(null);

  const load = useCallback(async () => {
    try { setA(await getAccount()); } catch (e) { errorToast("Could not load your account"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!a) return null;

  if (!a.connected || !a.matched) {
    return (
      <div className="portal-page">
        <h5 className="fw-bold">Account</h5>
        <div className="portal-empty">
          {a.reason || "Your account is not linked yet."} Please ask us and we will sort it out — nothing is wrong with your orders.
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <h5 className="fw-bold mb-3">Account</h5>

      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <div className="portal-card h-100">
            <div className="small text-muted">Outstanding</div>
            <div className="fs-5 fw-bold">{inr(a.outstanding)}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="portal-card h-100">
            <div className="small text-muted">Overdue</div>
            <div className={`fs-5 fw-bold ${a.overdue > 0 ? "text-danger" : ""}`}>{inr(a.overdue)}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="portal-card h-100">
            <div className="small text-muted">Open invoices</div>
            <div className="fs-5 fw-bold">{a.openInvoices}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="portal-card h-100">
            <div className="small text-muted">Quotations open</div>
            <div className="fs-5 fw-bold">{a.openEstimates}</div>
          </div>
        </div>
      </div>

      {a.ageing.length > 1 && (
        <div className="portal-card mb-3">
          <div className="small fw-semibold mb-2">How long it has been owed</div>
          <div className="d-flex flex-wrap gap-3">
            {a.ageing.map((b) => (
              <div key={b.label}>
                <div className="small text-muted">{b.label}</div>
                <div className={b.label === "over 90 days" ? "fw-bold text-danger" : "fw-semibold"}>{inr(b.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="portal-card mb-3 p-0">
        <div className="p-2 border-bottom small fw-semibold">Invoices</div>
        {a.invoices.length ? a.invoices.map((i) => (
          <div key={i.number} className="d-flex justify-content-between align-items-center p-2 border-bottom">
            <div>
              <div className="fw-semibold">{i.number}</div>
              <div className="small text-muted">
                {when(i.date)} · due {when(i.dueDate)}
                {i.overdueBy > 0 && <span className="text-danger"> · {i.overdueBy} days late</span>}
              </div>
            </div>
            <div className="text-end">
              <div className="fw-semibold">{inr(i.total)}</div>
              {i.balance > 0
                ? <div className="small text-danger">{inr(i.balance)} due</div>
                : <span className={`badge bg-${STATUS[i.status] || "secondary"}`}>{i.status.replace(/_/g, " ")}</span>}
            </div>
          </div>
        )) : <div className="p-3 text-muted small">No invoices yet.</div>}
      </div>

      <div className="portal-card p-0">
        <div className="p-2 border-bottom small fw-semibold">Quotations</div>
        {a.estimates.length ? a.estimates.map((e) => (
          <div key={e.number} className="d-flex justify-content-between align-items-center p-2 border-bottom">
            <div>
              <div className="fw-semibold">{e.number}</div>
              <div className="small text-muted">{when(e.date)}{e.expiry ? ` · valid to ${when(e.expiry)}` : ""}</div>
            </div>
            <div className="text-end">
              <div className="fw-semibold">{inr(e.total)}</div>
              <span className={`badge bg-${STATUS[e.status] || "secondary"}`}>{String(e.status).replace(/_/g, " ")}</span>
            </div>
          </div>
        )) : <div className="p-3 text-muted small">No quotations yet.</div>}
      </div>

      <div className="small text-muted mt-3">
        These figures come straight from our accounts. If something looks wrong, tell us and we will check it rather than argue with the screen.
      </div>
    </div>
  );
};

export default AccountPage;
